// Utility helpers for parsing Move F64 values that come back from the Sui RPC
// representation. The Move struct exposes a mantissa, exponent bits and a
// boolean that encodes the sign. We reuse the conversion used in the admin
// tooling so the math stays consistent across the app.

export function parseF64Value(value: any): number {
	if (value == null) {
		return 0;
	}

	// Accept already parsed primitives
	if (typeof value === 'number') {
		return value;
	}
	if (typeof value === 'string') {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : 0;
	}

	// Some RPC responses wrap the struct in { value: { fields: ... } }
	if ('value' in value && value.value !== undefined) {
		return parseF64Value(value.value);
	}
	if ('fields' in value === false) {
		return 0;
	}

	const fields = (value as any).fields ?? {};
	const isNegative = Boolean(fields.is_negative);
	const mantissa = Number(fields.mant ?? '0');
	if (!Number.isFinite(mantissa) || mantissa === 0) {
		return 0;
	}

	const expBitsStr = fields.exp?.fields?.bits ?? fields.exp?.bits ?? '0';
	let expBigInt: bigint;
	try {
		expBigInt = BigInt(expBitsStr);
	} catch {
		return 0;
	}

	const TWO_POW_64 = BigInt('18446744073709551616');
	const I64_MAX = BigInt('9223372036854775807');
	const expI64 = expBigInt > I64_MAX ? Number(expBigInt - TWO_POW_64) : Number(expBigInt);

	const sign = isNegative ? -1 : 1;
	const exponent = expI64 - 52; // F64 stores mantissa without the implicit bit

	const result = sign * mantissa * Math.pow(2, exponent);
	return Number.isFinite(result) ? result : 0;
}

export function parseF64ValueAsFixed(value: any, fractionDigits: number = 6): string {
	return parseF64Value(value).toFixed(fractionDigits);
}
